/**
 * Floral letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const floralLettersPack: ToilePack = {
    slug: 'floral-letters',
    category: 'illustration',
    name: 'Floral letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'roses', label: 'Rose letter', subject: 'a capital "E" whose strokes are filled with small pink roses and green leaves, the letter\'s edge kept clean'},
        {key: 'wreath', label: 'Wildflower wreath', subject: 'a serif capital "L" with a crescent of wildflowers, daisies and lavender curling around its left side'},
        {key: 'botanical', label: 'Botanical', subject: 'a slim capital "S" with a single eucalyptus stem and two white blossoms twining through its curves'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor flowers with loose washes, fine petal veins and gentle edges', palette: 'Blush pink, sage green and cream on a white paper background.', framing: 'The letter is centered with a wide margin of empty paper.'},
        {key: 'botanical', label: 'Botanical ink', style: 'a botanical illustration in fine black ink lines with delicate flat color', palette: 'Muted rose, olive green and ink black on a white background.', framing: 'The letter is centered with a generous margin.'},
        {key: 'papercut', label: 'Paper cut', style: 'flat vector flowers in bold paper-cut shapes with hard edges and no outlines', palette: 'Coral, mustard and deep green on a solid cream background.', framing: 'The letter is centered with an even margin.'},
    ],
    directions: ['peonies', 'blue flowers', 'dark background'],
    directionHint: 'try: peonies',
    seo: {
        title: 'Floral Letters: Roses, Wildflowers, Botanical Capitals',
        metaDescription: 'Nine floral letter designs: single capitals made of roses, wildflowers and leaves, in watercolor, botanical ink and paper cut.',
        h1: 'Floral letters: a rose letter, a wildflower wreath, a botanical capital',
        tagline: 'Single capitals made of or wrapped in flowers, in three artistic treatments.',
        intro: 'Floral letters are a single letter made of or wrapped in flowers, for a nursery wall, a wedding monogram or a card. The nine designs show a capital E filled with pink roses and leaves, a capital L with a crescent of wildflowers and lavender, and a slim capital S with a eucalyptus stem and two white blossoms. Each is drawn as soft watercolor, fine botanical ink or flat paper-cut shapes. Ask for peonies, blue flowers or a dark background.',
        useCases: [
            'a letter for a nursery wall',
            'a wedding monogram',
            'an initial on a card or a gift tag',
            'a framed print for a bedroom',
        ],
        faq: [
            {q: 'Which letters are shown?', a: 'E, L and S. Each letter is shown in all three treatments.'},
            {q: 'Can I get a different letter?', a: 'The samples show E, L and S. A request for another letter is not a promised result.'},
            {q: 'What is the botanical ink style?', a: 'A fine black ink drawing of plants, with delicate flat color.'},
            {q: 'Is the letter still readable?', a: 'Yes. Each capital is kept clear enough to read at a glance.'},
        ],
    },
}
